// Copyright (c) 2024-2026 Echonum LLC. All rights reserved.
// Product: Exploit Score ETS (Exploit Threat Score)
// Version: 2.8.6
//
// This file is part of Exploit Score ETS, proprietary software developed by
// Echonum LLC. Unauthorized copying, distribution, modification, or use of
// this file, via any medium, is strictly prohibited without express written
// permission from Echonum LLC.
//
// Confidential and Proprietary.

import { useState } from 'react'
import { Link } from 'react-router-dom'
import { Loader2, CheckCircle2 } from 'lucide-react'
import toast from 'react-hot-toast'
import api from '../api/client'
import Turnstile from '../components/Turnstile'

// Public Cloudflare Turnstile site key (safe to embed). When unset — e.g. local
// dev — the widget is skipped and the backend also skips verification.
const CAPTCHA_SITE_KEY = import.meta.env.VITE_TRIAL_CAPTCHA_SITE_KEY as string | undefined

// Licensed industries (canonical names the scorer recognizes).
const INDUSTRIES = [
  'healthcare', 'communications', 'energy', 'finance', 'government',
  'retail', 'manufacturing', 'automotive', 'education', 'pharmaceutical',
]

const COMPANY_SIZES = ['1-10', '11-50', '51-200', '201-500', '501-1000', '1000+']

export default function RequestTrial() {
  const [formData, setFormData] = useState({
    email: '',
    company_name: '',
    contact_name: '',
    phone: '',
    industry: '',
    company_size: '',
    use_case: '',
  })
  const [isLoading, setIsLoading] = useState(false)
  const [submitted, setSubmitted] = useState(false)
  const [captchaToken, setCaptchaToken] = useState('')
  const [captchaResetKey, setCaptchaResetKey] = useState(0)

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>
  ) => {
    setFormData({ ...formData, [e.target.name]: e.target.value })
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (CAPTCHA_SITE_KEY && !captchaToken) {
      toast.error('Please complete the verification challenge.')
      return
    }
    setIsLoading(true)
    try {
      await api.post('/api/portal/trial/request', {
        email: formData.email,
        company_name: formData.company_name,
        contact_name: formData.contact_name || undefined,
        phone: formData.phone || undefined,
        industry: formData.industry || undefined,
        company_size: formData.company_size || undefined,
        use_case: formData.use_case || undefined,
        captcha_token: captchaToken || undefined,
      })
      setSubmitted(true)
    } catch (error: any) {
      const message = error.response?.data?.detail || 'Could not submit your request. Please try again.'
      toast.error(message)
      // Turnstile tokens are single-use — remount the widget so the user can retry.
      setCaptchaToken('')
      setCaptchaResetKey((k) => k + 1)
    } finally {
      setIsLoading(false)
    }
  }

  if (submitted) {
    return (
      <div className="min-h-screen flex items-center justify-center p-4 py-12">
        <div className="w-full max-w-md text-center">
          <div className="inline-flex items-center justify-center mb-4">
            <img src="/logo.png" alt="Exploit Score" className="h-16 w-auto" />
          </div>
          <div className="card">
            <CheckCircle2 className="w-14 h-14 text-green-500 mx-auto mb-4" />
            <h1 className="text-2xl font-bold mb-2">Request received</h1>
            <p className="text-gray-400">
              Thanks for your interest in Exploit Score ETS. Our team will review your
              request and respond within one business day with your trial license and
              setup instructions.
            </p>
            <Link to="/login" className="btn btn-primary w-full mt-6 inline-flex justify-center">
              Back to sign in
            </Link>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen flex items-center justify-center p-4 py-12">
      <div className="w-full max-w-md">
        <div className="text-center mb-8">
          <div className="inline-flex items-center justify-center mb-4">
            <img src="/logo.png" alt="Exploit Score" className="h-16 w-auto" />
          </div>
          <h1 className="text-2xl font-bold">Start your free trial</h1>
          <p className="text-gray-400 mt-2">
            Tell us a bit about your organization and we'll set you up with a
            time-limited trial license &mdash; usually within one business day.
          </p>
        </div>

        <form onSubmit={handleSubmit} className="card space-y-5">
          <div>
            <label className="block text-sm font-medium mb-2">Business email *</label>
            <input
              type="email"
              name="email"
              value={formData.email}
              onChange={handleChange}
              placeholder="you@company.com"
              required
            />
            <p className="text-xs text-gray-500 mt-1">
              Please use your work email &mdash; personal email domains aren't accepted.
            </p>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium mb-2">Company *</label>
              <input
                type="text"
                name="company_name"
                value={formData.company_name}
                onChange={handleChange}
                placeholder="Company name"
                required
              />
            </div>
            <div>
              <label className="block text-sm font-medium mb-2">Your name</label>
              <input
                type="text"
                name="contact_name"
                value={formData.contact_name}
                onChange={handleChange}
                placeholder="Full name"
              />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium mb-2">Industry</label>
              <select name="industry" value={formData.industry} onChange={handleChange}>
                <option value="">Select…</option>
                {INDUSTRIES.map((ind) => (
                  <option key={ind} value={ind}>
                    {ind.charAt(0).toUpperCase() + ind.slice(1)}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium mb-2">Company size</label>
              <select name="company_size" value={formData.company_size} onChange={handleChange}>
                <option value="">Select…</option>
                {COMPANY_SIZES.map((s) => (
                  <option key={s} value={s}>{s} employees</option>
                ))}
              </select>
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium mb-2">Phone</label>
            <input
              type="tel"
              name="phone"
              value={formData.phone}
              onChange={handleChange}
              placeholder="+1 (555) 123-4567"
            />
          </div>

          <div>
            <label className="block text-sm font-medium mb-2">What would you like to evaluate?</label>
            <textarea
              name="use_case"
              value={formData.use_case}
              onChange={handleChange}
              placeholder="Tell us about your environment and goals (optional)"
              rows={3}
            />
          </div>

          {CAPTCHA_SITE_KEY && (
            <Turnstile
              key={captchaResetKey}
              siteKey={CAPTCHA_SITE_KEY}
              onToken={setCaptchaToken}
            />
          )}

          <button type="submit" disabled={isLoading} className="btn btn-primary w-full">
            {isLoading ? (
              <>
                <Loader2 className="w-5 h-5 animate-spin" />
                Submitting…
              </>
            ) : (
              'Request trial'
            )}
          </button>
        </form>

        <p className="text-center text-gray-400 mt-6">
          Already have an account?{' '}
          <Link to="/login" className="text-primary-500 hover:underline">
            Sign in
          </Link>
        </p>
      </div>
    </div>
  )
}
